<template>
  <div>
    <transition name="news-fade">
      <div v-if="isVisible && isOpen" class="news-float">
        <div class="news-float__backdrop" @click="close"></div>
        <div class="card news-float__card" role="dialog" aria-label="Noticias recientes" @click.stop>
          <header class="card-header">
            <p class="card-header-title">Novedades</p>
            <button class="card-header-icon news-float__close" type="button" aria-label="Cerrar" @click="close">
              <span class="icon" aria-hidden="true">&times;</span>
            </button>
          </header>

          <div ref="scrollContainer" class="card-content news-float__body" @scroll="onScroll">
            <article v-for="item in items" :key="item.id" class="news-float__item">
              <p v-if="item.publishDate" class="is-size-7 news-float__muted">{{ humanDate(item.publishDate, 'DD MMMM YYYY', null, 'es') }}</p>
              <p class="has-text-weight-semibold">{{ item.title }}</p>
              <p class="is-size-7 news-float__summary">{{ item.shortDescription }}</p>
              <button class="news-float__link" type="button" @click="openDetails(item)">
                <span>Ver más</span>
                <span class="icon is-small"><i class="fas fa-external-link-alt"></i></span>
              </button>
              <figure v-if="item.image" class="image news-float__image">
                <img :src="item.image" :alt="item.title" loading="lazy">
              </figure>
            </article>

            <div v-if="loading" class="has-text-centered is-size-7">Cargando...</div>
            <div v-else-if="errorMessage" class="has-text-centered is-size-7 has-text-danger">
              {{ errorMessage }}
            </div>
            <div v-else-if="!items.length" class="has-text-centered is-size-7">
              Por el momento no tenemos nada que mostrarte.
            </div>
            <div v-else-if="!hasNext" class="has-text-centered is-size-7 mt-2 is-italic">
              No hay más noticias por ahora.
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="news-detail">
      <div v-if="showDetailModal" class="news-detail">
        <div class="news-detail__backdrop" @click="closeDetails"></div>
        <div class="news-detail__panel" role="dialog" aria-label="Detalle de noticia">
          <header class="news-detail__header">
            <div>
              <p class="news-detail__title">{{ activeItem.title }}</p>
              <p v-if="activeItem.publishDate" class="news-detail__date">{{ humanDate(activeItem.publishDate, 'DD MMMM YYYY', null, 'es') }}</p>
            </div>
            <button class="news-detail__close" type="button" aria-label="Cerrar" @click="closeDetails">
              <span aria-hidden="true">&times;</span>
            </button>
          </header>

          <div class="news-detail__content">
            <section class="news-detail__main">
              <figure v-if="activeItem.image" class="news-detail__media">
                <img :src="activeItem.image" :alt="activeItem.title">
              </figure>
              <div class="news-detail__summary" v-html="activeItem.description"></div>
            </section>

            <aside class="news-detail__aside">
              <div class="news-detail__card">
                <p class="news-detail__label">Resumen</p>
                <p class="news-detail__text">{{ activeItem.shortDescription }}</p>
              </div>
              <div class="news-detail__card" v-if="activeItem.link">
                <p class="news-detail__label">Enlace</p>
                <a class="news-detail__link" :href="activeItem.link" target="_blank" rel="noopener">
                  Abrir publicación completa
                </a>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import mixins from '../../mixins/formats'
import amplitude from '@/utils/amplitude'

export default {
  name: 'NewsFloating',
  mixins: [mixins],
  props: {
    isVisible: {
      type: Boolean,
      default: true
    },
    isOpen: {
      type: Boolean,
      default: false
    },
    pageSize: {
      type: Number,
      default: 5
    },
    fetchNewsPage: {
      type: Function,
      default: null
    }
  },
  data () {
    return {
      items: [],
      cursor: null,
      hasNext: true,
      loading: false,
      errorMessage: '',
      itemIds: new Set(),
      isListening: false,
      showDetailModal: false,
      activeItem: {}
    }
  },
  mounted () {
    this.loadMore()
    if (this.isOpen) {
      this.bindKeydown()
    }
  },
  beforeDestroy () {
    this.unbindKeydown()
  },
  watch: {
    isOpen (value) {
      if (value) {
        this.bindKeydown()
      } else {
        this.unbindKeydown()
      }
    }
  },
  computed: {
    ...mapState({
      authentication: state => state.authentication,
      financialCompany: state => state.financialCompany.financialCompany
    })
  },
  methods: {
    close () {
      this.$emit('close')
    },
    handleKeydown (event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        if (this.showDetailModal) {
          this.closeDetails()
          return
        }
        this.close()
      }
    },
    openDetails (item) {
      this.activeItem = item || {}
      this.showDetailModal = true
      amplitude.track('newsClicked', {
        newsId: item.id,
        newsTitle: item.title,
        financial: this.financialCompany.identifier,
        userId: this.authentication.userId
      })
    },
    closeDetails () {
      this.showDetailModal = false
      this.activeItem = {}
    },
    bindKeydown () {
      if (this.isListening) return
      window.addEventListener('keydown', this.handleKeydown)
      this.isListening = true
    },
    unbindKeydown () {
      if (!this.isListening) return
      window.removeEventListener('keydown', this.handleKeydown)
      this.isListening = false
    },
    onScroll () {
      const container = this.$refs.scrollContainer
      if (!container || this.loading || !this.hasNext) return
      const threshold = 120
      const distance = container.scrollHeight - (container.scrollTop + container.clientHeight)
      if (distance <= threshold) {
        this.loadMore()
      }
    },
    async loadMore () {
      if (this.loading || !this.hasNext) return
      this.loading = true
      this.errorMessage = ''
      try {
        const loader = this.fetchNewsPage
        const response = await loader({ cursor: this.cursor, limit: this.pageSize })
        const items = response.items || []
        items.forEach(item => {
          if (!this.itemIds.has(item.id)) {
            this.itemIds.add(item.id)
            this.items.push(item)
          }
        })
        this.cursor = response.nextCursor || (response.pageInfo && response.pageInfo.endCursor) || null
        if (typeof response.hasNext === 'boolean') {
          this.hasNext = response.hasNext
        } else if (response.pageInfo && typeof response.pageInfo.hasNextPage === 'boolean') {
          this.hasNext = response.pageInfo.hasNextPage
        } else {
          this.hasNext = items.length >= this.pageSize
        }
      } catch (error) {
        this.errorMessage = (error && error.message) ? error.message : 'No hay más noticias por ahora.'
        this.hasNext = false
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.news-float {
  position: fixed;
  inset: 0;
  z-index: 35;
}

.news-float__backdrop {
  position: absolute;
  inset: 0;
  background: transparent;
}

.news-float__card {
  position: absolute;
  top: 88px;
  right: 24px;
  width: 360px;
  background: #222D3B;
  color: #eef2ff;
  border: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.35);
  max-height: calc(100vh - 140px);
  overflow: hidden;
}

.news-float__card :deep(.card-header) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.news-float__card :deep(.card-header-title) {
  color: #ffffff;
}

.news-float__close {
  color: #ffffff;
  background: transparent;
  border: none;
}

.news-float__body {
  overflow-y: auto;
  max-height: calc(100vh - 220px);
}

.news-float__item:not(:first-child) {
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  padding-top: 0.75rem;
  margin-top: 0.75rem;
}

.news-float__image {
  margin-top: 0.5rem;
}

.news-float__muted {
  color: rgba(255, 255, 255, 0.7);
}

.news-float__summary {
  color: rgba(255, 255, 255, 0.85);
}

.news-float__link {
  color: #b3c7ff;
  background: transparent;
  border: none;
  padding: 0;
  width: 100%;
  text-decoration: underline;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.35rem;
  cursor: pointer;
}

.news-float__link:focus,
.news-float__link:focus-visible {
  outline: none;
  box-shadow: none;
}

@media (max-width: 1024px) {
  .news-float__card {
    right: 16px;
    width: 320px;
  }
}

@media (max-width: 768px) {
  .news-float__card {
    top: 72px;
    right: 8px;
    left: 8px;
    width: auto;
  }
}

.news-fade-enter-active .news-float__card,
.news-fade-leave-active .news-float__card {
  transition: opacity 180ms ease, transform 180ms ease;
}

.news-fade-enter .news-float__card,
.news-fade-leave-to .news-float__card {
  opacity: 0;
  transform: translateX(16px);
}

.news-detail {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.news-detail__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(10, 15, 28, 0.65);
  backdrop-filter: blur(10px);
}

.news-detail__panel {
  position: relative;
  z-index: 1;
  width: 92vw;
  max-width: 980px;
  max-height: 88vh;
  background: linear-gradient(160deg, #0f172a 0%, #111827 45%, #0b1220 100%);
  color: #e5e7eb;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  box-shadow: 0 30px 70px rgba(2, 6, 23, 0.65);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.news-detail__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px 12px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.16);
}

.news-detail__title {
  font-size: 20px;
  font-weight: 700;
  color: #f8fafc;
  margin: 0 0 6px;
}

.news-detail__date {
  font-size: 12px;
  color: rgba(226, 232, 240, 0.7);
}

.news-detail__close {
  background: rgba(148, 163, 184, 0.12);
  border: none;
  color: #ffffff;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  font-size: 20px;
  cursor: pointer;
}

.news-detail__content {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(220px, 1fr);
  gap: 20px;
  padding: 20px 24px 24px;
  overflow: hidden;
}

.news-detail__main {
  overflow-y: auto;
  padding-right: 8px;
}

.news-detail__media {
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 16px;
}

.news-detail__media img {
  width: 100%;
  display: block;
}

.news-detail__summary {
  font-size: 14px;
  color: rgba(226, 232, 240, 0.9);
}
/* El HTML de la noticia se inyecta con v-html, así que no pasa por el
   compilador de plantillas de Vue y estos hijos nunca reciben el atributo
   scoped — hay que usar :deep() para alcanzarlos. Bulma define un color
   oscuro global para "strong" (casi negro) que gana sobre el color claro
   heredado de este contenedor, dejando el texto en negritas invisible
   sobre el fondo navy del modal. */
.news-detail__summary :deep(p) { margin-bottom: 1rem; }
.news-detail__summary :deep(ul) { list-style: disc; padding-left: 1.5rem; }
.news-detail__summary :deep(ol) { list-style: decimal; padding-left: 1.5rem; }
.news-detail__summary :deep(strong),
.news-detail__summary :deep(b) { color: #f8fafc; }

.news-detail__aside {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.news-detail__card {
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 12px;
  padding: 12px;
}

.news-detail__label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(148, 163, 184, 0.8);
  margin-bottom: 6px;
}

.news-detail__text {
  font-size: 13px;
  color: rgba(226, 232, 240, 0.85);
}

.news-detail__link {
  color: #93c5fd;
  font-size: 13px;
  text-decoration: underline;
}

.news-detail-enter-active,
.news-detail-leave-active {
  transition: opacity 200ms ease;
}

.news-detail-enter .news-detail__panel,
.news-detail-leave-to .news-detail__panel {
  transform: translateY(14px);
  opacity: 0;
  transition: transform 200ms ease, opacity 200ms ease;
}

@media (max-width: 900px) {
  .news-detail__content {
    grid-template-columns: 1fr;
  }

  .news-detail__aside {
    flex-direction: row;
    flex-wrap: wrap;
  }
}

@media (max-width: 640px) {
  .news-detail__panel {
    width: 95vw;
  }

  .news-detail__content {
    padding: 16px;
  }
}
.mt-2 {
  margin-top: 1.5rem;
}
</style>
